// Considered: ComposerShellOrganism's side panel — rejected (spec Reuse Audit): that panel is
// bound to a CompositionHost canvas and its Inspect/Agent/Wires modes; this is a tabbed aside over
// a section document. Source: type-composer.jsx:1795-1807 (the tabbed aside: Sample record ·
// Agent · Inherited), generalized per EAINT-921 Phase 2 from the rail pattern prototype's own
// inspector aside (`type-rail/trail-composer.jsx:1779-1904`), which adds a `selected` block's own
// head (kind · title · sub · close) IN PLACE of the tab strip while a block is being edited.
//
// Relocated + renamed from `organisms/studio/ComposerSideRailOrganism` (EAINT-897/EAINT-895) and
// generalized so a second consumer (the Aspect Editor, Phase 4) can host it without a Schema
// import: `definition: Schema | null` became `entity: InspectorRailEntity | null` (identity only),
// the Sample tab's BODY became the `sampleContent` slot (mirroring the `inheritedContent` slot this
// organism already had) rather than a hardcoded `SampleRecordPanelMolecule`/`SampleRecordDialogMolecule`/
// `useSandboxSphere` bundle — those stay Schema-typed and now live in the composer, which is where
// Schema-specific concerns belong. `AGENT_THREAD_PARITY` and the two i18n namespaces became props
// the composer supplies, so no `studio.typeEditor.*` literal is baked into this file. The
// `ComposerSideRailHandle` ref API (`focusSampleTab`, `focusAgentInput`, `focusAgentTurn`) is kept
// verbatim, including its exported name, so every existing caller only has to change its import.

import React, { forwardRef } from 'react';
import { useTranslation } from 'localization';
import AgentThreadOrganism, { type AgentThreadHandle, type AgentThreadParity } from '../composer/AgentThreadOrganism';
import type { AgentThreadItem } from '../../molecules/composer/AgentMessageMolecule';
import type {
  ComposerAgentTarget,
  ComposerAgentTypeEditProposal,
  ComposerGhostProvenance,
  ComposerTypeCritique,
} from '../../../composer/agentTransport';
import SegmentedControlAtom from '../../atoms/SegmentedControl/SegmentedControlAtom';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import InlineConfirmMolecule from '../../molecules/studio/InlineConfirmMolecule';
import { useDialogFocus } from '../../../hooks/useDialogFocus';
import './InspectorRailOrganism.scss';

type SideRailTab = 'sample' | 'agent' | 'inherits';

/** Stable identity so the default queue never re-renders the list for a prop that never changed. */
const EMPTY_PENDING_BLOCKS: { id: string; label: string; icon?: string }[] = [];

/** The entity the rail is inspecting — identity only, never the entity's own shape. A schema
 * carries a `Schema`, an aspect an `AspectRow` (Phase 4); this organism needs neither, only what
 * every host can name: what kind of thing it is, and what to call it. */
export interface InspectorRailEntity {
  kind: string;
  id?: string;
  systemName?: string;
  label: string;
}

/** The rail head a selected block renders INSTEAD of the tab strip
 * (`type-rail/trail-composer.jsx:1781-1791`): kind eyebrow, title, optional sub, and a close
 * action that hands control back to whatever set the selection.
 *
 * The rail renders `body` whatever `locked` says, exactly as the prototype renders `selected`
 * outside its `readOnly` guard: a selection is not inherently an editor (an inherited rule can be
 * inspected read-only), so the rail cannot know whether to drop it. The guard belongs to the
 * PRODUCER — every `setSel` in the prototype that opens an editor is `if (!readOnly)`
 * (`:1279`, `:1564`), and the composer fences its openers with `editable &&`. A host that sets a
 * selection carrying an editor on a locked entity has a permissions bug in its own opener; the
 * rail will not catch it. */
export interface InspectorRailIdle {
  /** Omit both `kind` and `title` for an idle rail whose body says everything itself (an empty
   * state carrying its own title), rather than stating it twice. */
  kind?: string;
  title?: string;
  body: React.ReactNode;
}
export interface InspectorRailSelection {
  kind: string;
  title: string;
  sub?: string;
  body: React.ReactNode;
}

export interface InspectorRailOrganismProps {
  entity: InspectorRailEntity | null;
  /**
   * A read-only type (platform-owned, sealed, sealed ancestor, no permission). The rail itself
   * still renders — `type-composer.jsx:1795` puts `<aside className="t-side">` outside every
   * read-only guard — but the Agent tab does not, because there is no draft to propose against.
   * That mirrors the prototype's `t.drafting` gate at `:1797`. Required (not defaulted): a host
   * that forgets to compute it is a permissions bug, not a reasonable default.
   */
  locked: boolean;
  /** i18n namespace for this rail's own strings (aria-label, inherited-tab copy, close action).
   * The composer supplies it so no `studio.typeEditor.*` literal is baked into this file — a
   * second host (Phase 4's Aspect Editor stub) supplies its own. */
  namespace: string;
  /** i18n namespace for the Agent tab's strings (queue, compose bar, origin-missing notice). */
  agentNamespace: string;
  /**
   * When present, replaces the tab strip and its panels with the selected block's own head and
   * body (`type-rail/trail-composer.jsx:1781-1791`) — the rail pattern's block-editing mode.
   * Wired up by the composer in Phase 3; this organism only needs to know how to render it.
   */
  selection?: InspectorRailSelection;
  /** Closes the current `selection`, returning the rail to its tab strip. */
  onCloseSelection?: () => void;
  /**
   * What the rail shows while nothing is selected, in place of its tab strip. For a host whose
   * rail is an editor and nothing else (the Aspect Editor: "Nothing selected · Pick a block"),
   * the Inherited tab would be a route to content it does not have. Same head as a selection,
   * without the close action, because there is nothing to close.
   */
  idle?: InspectorRailIdle;
  /** The Sample tab's body. A slot, not a hardcoded `SampleRecordPanelMolecule`: the sample-record
   * reading is Schema-specific and belongs to the composer, which is the only host that knows how
   * to build one. Absent (Phase 4's Aspect Editor stub), the Sample tab is not offered. */
  sampleContent?: React.ReactNode;
  agentEndpoint?: string;
  agentGetToken?: () => Promise<string | undefined>;
  agentTarget?: ComposerAgentTarget;
  agentDraftDigest?: string;
  onTypeEditProposal?: (proposal: ComposerAgentTypeEditProposal, provenance: ComposerGhostProvenance) => void;
  /** Ghosts awaiting Keep/Discard, across every section — drives the tab's count badge and
   * whether Approve all / Discard all render at all. */
  pendingGhostCount?: number;
  /** The blocks those ghosts sit in, in the document's own order — one row per block, each a
   * route to it (`:1856-1866`). The count alone tells the user how much is waiting and gives
   * them nowhere to go; the composer derives this from the same ghost list the count comes from. */
  pendingBlocks?: { id: string; label: string; icon?: string }[];
  onApproveAllGhosts?: () => void;
  /** Set while a batch approval runs: `kept` of `total` have landed so far. The queue's
   * controls are held and the progress is read out beside them until it clears. */
  approveAllProgress?: { kept: number; total: number } | null;
  onDiscardAllGhosts?: () => void;
  /** The count named in the inline discard-all confirm — everything `onDiscardAllGhosts` actually
   * clears, which can exceed `pendingGhostCount` (a ghost can sit in the list in a non-pending
   * status). Falls back to `pendingGhostCount` when the caller has nothing further to add. */
  discardAllCount?: number;
  /** How many facts arrive from the lineage — the Inherited tab's count (`:1804`). */
  inheritCount?: number;
  /** The Inherited tab's body. Phase 5 supplies it; the framing hint renders either way. */
  inheritedContent?: React.ReactNode;
  /** Story/test seam: mounts with a given tab selected rather than the derived default. */
  initialTab?: SideRailTab;
  /** Story/test seam: mounts the thread holding these turns rather than empty. */
  initialAgentItems?: AgentThreadItem[];
  /** Jumps to the composer section a turn names — the thread's `[see it]` (`:1875`). */
  onJumpToBlock?: (blockId: string) => void;
  /**
   * Reports what `AgentThreadOrganism`'s probe found, so the ENTRANCE to the agent path can be
   * gated on the same fact as the path itself. The rail dropped its Agent tab on a confirmed-
   * unavailable service while the composer header kept rendering an Agent button that opened
   * nothing — `focusAgentInput()` returns early on exactly this flag. Availability is probed
   * here because this is where the thread mounts; anything that offers a way in has to hear it.
   */
  onAgentAvailabilityChange?: (available: boolean) => void;
  /**
   * The agent's ambient critique, undismissed, across every block — the Agent tab's own
   * "Watching as you edit" panel (`type-rail/trail-composer.jsx:1879-1889`), distinct from a
   * block's own inline critique card (which the composer renders itself, filtered to that
   * block). Advisory only, same as its inline twin: reads and comments, never writes.
   */
  critique?: ComposerTypeCritique[];
  /** Dismisses one critique item — shared with whatever else reads `critique`, so a dismissal
   * here and a block's own inline dismissal stay in sync. */
  onDismissCritique?: (id: string) => void;
  /**
   * The contract ids the `side` region gives the thread's own elements
   * (`type-composer.jsx:1871-1898`): the turn's jump link and shared chip, and the compose bar's
   * input and send. The composer supplies its own paths; other hosts (the Context Builder, the
   * gallery) mount `AgentThreadOrganism` outside this decomposition entirely.
   */
  parity: AgentThreadParity;
}

/** Imperative handle so the footer's `[Preview sample record]` can move focus here (spec `:1912`),
 * and so a ghost row rendered outside this rail can send focus back into the thread's input
 * (spec Keyboard map: "Escape from a ghost returns to the thread"). Name kept verbatim across the
 * Phase 2 relocation — every existing caller only has to change its import path. */
export interface ComposerSideRailHandle {
  focusSampleTab: () => void;
  /** Opens the Inherited tab and moves focus to its panel — the index rail's "Inherited & derived"
   * entry is always a route here, never a section of its own. */
  focusInheritedTab: () => void;
  focusAgentInput: () => void;
  /** Opens the Agent tab on the turn a ghost's provenance line names. */
  focusAgentTurn: (turnId: string) => void;
  /**
   * Opens the Agent tab and submits `message` as a turn — the "Draft a new aspect" dialog's
   * `describe` route (EAINT-933 Phase 3.4): the agent's own tool call is what turns this into a
   * reviewable ghost, so nothing here writes directly. A no-op while the service is confirmed
   * unavailable, same guard `focusAgentInput` already applies.
   */
  submitAgentMessage: (message: string) => void;
}

const InspectorRailOrganism = forwardRef<ComposerSideRailHandle, InspectorRailOrganismProps>(
  (
    {
      entity,
      locked,
      namespace,
      agentNamespace,
      selection,
      onCloseSelection,
      idle,
      sampleContent,
      agentEndpoint,
      agentGetToken,
      agentTarget,
      agentDraftDigest,
      onTypeEditProposal,
      pendingGhostCount = 0,
      pendingBlocks = EMPTY_PENDING_BLOCKS,
      onApproveAllGhosts,
      approveAllProgress = null,
      onDiscardAllGhosts,
      discardAllCount,
      inheritCount = 0,
      inheritedContent,
      initialTab,
      initialAgentItems,
      onJumpToBlock,
      onAgentAvailabilityChange,
      critique,
      onDismissCritique,
      parity,
    },
    ref
  ) => {
    const { t } = useTranslation();
    const railRef = React.useRef<HTMLElement>(null);
    const headingRef = React.useRef<HTMLElement | null>(null);
    // Discard all is destructive and irreversible (AGENT-UX Rule 4) — it asks in place rather
    // than firing `onDiscardAllGhosts` straight off the button, the same non-modal pattern the
    // spec's InlineConfirmMolecule uses for every other consequential rail action.
    const [confirmingDiscardAll, setConfirmingDiscardAll] = React.useState(false);
    React.useEffect(() => {
      if (pendingGhostCount === 0) setConfirmingDiscardAll(false);
    }, [pendingGhostCount]);
    const setHeadingRef = React.useCallback((node: HTMLElement | null) => {
      headingRef.current = node;
    }, []);
    const agentThreadRef = React.useRef<AgentThreadHandle>(null);
    /**
     * A selection opens in place and leaves the document reachable, so it is a non-modal panel,
     * not a dialog — but it took over from one (the attach picker used to be a dialog with
     * Escape and focus return), and the keyboard rules do not change with the shell (ux-craft
     * A1). Escape raised inside the rail closes the selection; focus moves into the rail on
     * open and back to whatever opened it — the block's own control — on close. The host's
     * editor body may mount a render later than the head (the composer portals into it), so
     * the rail itself takes focus first and the editor's own `autoFocus` then carries it in.
     */
    useDialogFocus(!!selection, railRef, { trapFocus: false, onDismiss: onCloseSelection });

    /**
     * At a stacked (narrow) viewport the rail is not pinned — it is a column after the
     * document, so it can render below the fold entirely (spec Phase 3.3: "collapsed rail
     * expands, at 320px scrolls into view"). `useDialogFocus`'s own `focus()` call above
     * scrolls only as far as the newly-focused control needs; this runs after it (later hook,
     * later effect) so it wins the final position, bringing the rail's own head — kind, title,
     * close action — into view rather than leaving the reader to find it after just the first
     * control scrolled up.
     */
    React.useEffect(() => {
      if (!selection) return;
      railRef.current?.scrollIntoView?.({ block: 'start' });
    }, [selection]);

    // Default tab is `agent` while drafting (spec `ComposerSideRailOrganism` row). The rail now
    // mounts for a read-only type too (`:1795`), so "drafting" is no longer unconditional: it is
    // `!locked`, matching `tabOptions` below and the prototype's `t.drafting` gate at `:1797`.
    // Falls back to `sample` otherwise so there is never a tab pointing at content that can't
    // render — a locked type with an `agentTarget` would otherwise open on the agent panel while
    // the strip offers no Agent option to leave it by.
    const [tab, setTab] = React.useState<SideRailTab>(
      initialTab ?? (!locked && agentTarget ? 'agent' : 'sample'),
    );
    const [focusToken, setFocusToken] = React.useState(0);
    // Optimistic until `AgentThreadOrganism`'s own probe reports otherwise — availability is
    // probed, not assumed, but there is nothing to assume "unavailable" from before the probe
    // resolves either. A confirmed-unavailable service hides the Agent tab entirely rather than
    // leaving it mounted on a permanently-empty panel (`agentUnavailableHidesRailAndKeepsManualPathComplete`).
    const [agentAvailable, setAgentAvailable] = React.useState(true);
    /** One signal, two consumers: this rail's tab strip and whatever offered the way in (the
     * composer header's Agent button). They were gated on different facts, so a confirmed-
     * unavailable service hid the destination and left the entrance enabled. */
    const handleAgentAvailability = React.useCallback(
      (available: boolean) => {
        setAgentAvailable(available);
        onAgentAvailabilityChange?.(available);
      },
      [onAgentAvailabilityChange],
    );
    const pendingAgentFocusRef = React.useRef(false);
    /** A turn to reveal once the Agent tab is showing. The panel stays mounted, so the thread's
     * handle already exists at request time — but a `hidden` element cannot take focus, so the
     * reveal has to land first. */
    const pendingTurnFocusRef = React.useRef<string | null>(null);
    /** The tab panel (Sample or Inherited) to focus once the tab strip is showing again. */
    const pendingPanelFocusRef = React.useRef<'sample' | 'inherits' | null>(null);
    const inheritedPanelRef = React.useRef<HTMLDivElement | null>(null);
    /**
     * While a `selection` owns the rail the whole tab body is `hidden`, so none of the handle's
     * targets can take focus. Every handle method therefore asks the host to close the selection
     * and QUEUES its focus; the effects below consume the queue only once the strip is back.
     * Without this the header's Agent button, a ghost's provenance link and the footer's
     * [Preview sample record] were silent no-ops exactly while an editor was open in the rail.
     */
    const selectionOpen = !!selection;

    /** A provenance link asked for a turn this thread no longer holds. Stated, not swallowed. */
    const [originMissing, setOriginMissing] = React.useState(false);
    const originMissingRef = React.useRef<HTMLParagraphElement>(null);

    // The success path lands focus on the turn; the failure path has to land it somewhere too,
    // or a keyboard author is left on a button in another region with the answer out of reach.
    React.useEffect(() => {
      if (originMissing) originMissingRef.current?.focus();
    }, [originMissing]);

    React.useEffect(() => {
      if (agentAvailable && !locked) return;
      // The Agent tab is about to be taken away, so the effect that would consume these will
      // never run. Left set, they fire against a later, unrelated visit: the service recovers,
      // the author opens the tab to ask something new, and focus jumps to a stale turn.
      pendingAgentFocusRef.current = false;
      pendingTurnFocusRef.current = null;
      if (tab === 'agent') setTab('sample');
    }, [agentAvailable, locked, tab]);

    React.useEffect(() => {
      if (tab !== 'agent') {
        setOriginMissing(false);
        return;
      }
      // The panel is hidden until the host closes the selection; the queue waits for that.
      if (selectionOpen) return;
      if (pendingAgentFocusRef.current) {
        pendingAgentFocusRef.current = false;
        agentThreadRef.current?.focusInput();
      }
      if (pendingTurnFocusRef.current) {
        const turnId = pendingTurnFocusRef.current;
        pendingTurnFocusRef.current = null;
        setOriginMissing(!agentThreadRef.current?.focusTurn(turnId));
      }
    }, [tab, selectionOpen]);

    React.useImperativeHandle(ref, () => ({
      focusSampleTab: () => {
        if (selectionOpen) onCloseSelection?.();
        pendingPanelFocusRef.current = 'sample';
        setTab('sample');
        setFocusToken((n) => n + 1);
      },
      focusInheritedTab: () => {
        if (selectionOpen) onCloseSelection?.();
        pendingPanelFocusRef.current = 'inherits';
        setTab('inherits');
        setFocusToken((n) => n + 1);
      },
      focusAgentInput: () => {
        // There is no Agent tab to reach while the service is confirmed unavailable, and
        // queueing anyway is worse than doing nothing: the guard effect below re-runs on the
        // `setTab` and wipes the queue, so the call became a silent no-op — the exact failure
        // `originMissing` was added to remove, one layer up.
        if (!agentAvailable || !agentTarget) return;
        if (selectionOpen) onCloseSelection?.();
        if (tab === 'agent' && !selectionOpen) {
          agentThreadRef.current?.focusInput();
        } else {
          pendingAgentFocusRef.current = true;
          setTab('agent');
        }
      },
      focusAgentTurn: (turnId: string) => {
        // Nothing to reach and nowhere to say so: the notice lives INSIDE the agent panel,
        // and there is no Agent tab to bring it into view while the service is confirmed
        // unavailable with nothing yet said. Queueing instead would be actively worse (the
        // guard effect wipes it and bounces the tab back), so this returns.
        if (!agentAvailable || !agentTarget) return;
        if (selectionOpen) onCloseSelection?.();
        if (tab === 'agent' && !selectionOpen) {
          setOriginMissing(!agentThreadRef.current?.focusTurn(turnId));
        } else {
          pendingTurnFocusRef.current = turnId;
          setTab('agent');
        }
      },
      submitAgentMessage: (message: string) => {
        // Same "nothing to reach" guard as every other agent entrance — there is no service to
        // send a turn to, and queueing it would only resurface once the author has moved on.
        if (!agentAvailable || !agentTarget) return;
        if (selectionOpen) onCloseSelection?.();
        // The thread panel stays mounted (`hidden`, not unmounted) once it has rendered once,
        // same fact `focusAgentTurn`'s reveal relies on — so the handle already exists to send
        // through even before the tab switch below has painted.
        setTab('agent');
        agentThreadRef.current?.sendMessage(message);
      },
    }));

    React.useEffect(() => {
      if (focusToken === 0 || selectionOpen || !pendingPanelFocusRef.current) return;
      const panel = pendingPanelFocusRef.current === 'inherits' ? inheritedPanelRef.current : headingRef.current;
      pendingPanelFocusRef.current = null;
      panel?.scrollIntoView?.({ block: 'nearest' });
      panel?.focus();
    }, [focusToken, selectionOpen]);

    // `:1796-1799` — Sample record, Agent (only while an authored draft can be proposed
    // against — `:1797` gates it on `t.drafting`), Inherited. The Agent tab is dropped rather
    // than greyed when the type is read-only or the service is confirmed unavailable; the other
    // two always stand, for a read-only type as much as a draft.
    // `datum: 'side.label#13'` (not the bare `side.label`) — the contract assigns numeric
    // suffixes per distinct occurrence of a generic id in the prototype, and `side.label`
    // unsuffixed names an unrelated picker-shell `<CSection>` heading
    // (`side > panel > group > <CSection> > group:children`); `#13` is this tab strip's
    // `each([[...]])` template entry (`type-rail/trail-composer.jsx:1797`). PAR-4/6 match
    // `data-datum` by exact id, so the unsuffixed value never resolved to this tab at all.
    const tabOptions = [
      ...(sampleContent
        ? [{ value: 'sample' as const, label: t(`${namespace}.sampleTabLabel`), datum: 'side.label#13' }]
        : []),
      ...(!locked && agentTarget && agentAvailable
        ? [
            {
              value: 'agent' as const,
              label: t(`${agentNamespace}.tabLabel`),
              datum: 'side.label#13',
              // `:1803` puts `{pending.length}` INSIDE the Agent tab, and the contract's container
              // for `side.pending-length` is `tabs > each([...])` — the option, not the strip. As a
              // sibling of the whole control it was a number attached to nothing: its guard was
              // `pendingGhostCount > 0` while the tab's is `agentTarget && agentAvailable`, so a
              // draft with pending ghosts and no reachable agent rendered a bare count with no tab
              // under it.
              badge:
                pendingGhostCount > 0
                  ? {
                      label: String(pendingGhostCount),
                      ariaLabel: t(`${agentNamespace}.pendingProposals`, { count: pendingGhostCount }),
                      datum: 'side.pending-length',
                      kind: 'count',
                      testId: 'composer-side-rail-ghost-count',
                    }
                  : undefined,
            },
          ]
        : []),
      {
        value: 'inherits' as const,
        // `:1804` renders the count beside the label; the tab strip has no badge slot, so it
        // reads inline rather than being dropped.
        label:
          inheritCount > 0
            ? t(`${namespace}.inheritedTabLabelWithCount`, { count: inheritCount })
            : t(`${namespace}.inheritedTabLabel`),
        datum: 'side.label#13',
      },
    ];

    const effectiveTab = tabOptions.some((option) => option.value === tab) ? tab : (tabOptions[0]?.value ?? 'inherits');

    return (
      <aside
        ref={railRef}
        className="composer-side-rail"
        data-testid="composer-side-rail"
        aria-label={entity ? t(`${namespace}.ariaLabel`, { name: entity.label }) : t(`${namespace}.ariaLabel`)}
      >
        {selection && (
          <>
            <div
              className="composer-side-rail__head"
              data-container="side > rail-head"
              data-testid="composer-side-rail-head"
              data-datum="side.attach"
              data-kind="heading"
            >
              <span className="composer-side-rail__head-kind" data-datum="side.attach#2" data-kind="label">
                {selection.kind}
              </span>
              <b className="composer-side-rail__head-title" data-datum="side.attach-an-aspect">
                {selection.title}
              </b>
              {selection.sub && (
                <span className="composer-side-rail__head-sub" data-datum="side.blocks-the-save" data-kind="hint">
                  {selection.sub}
                </span>
              )}
              <ButtonAtom
                variant="text"
                shape="icon-square"
                size="small"
                icon="pi pi-times"
                aria-label={t(`${namespace}.closeEditor`)}
                onClick={onCloseSelection}
                data-datum="side.close-the-editor"
                data-kind="button"
                data-testid="composer-side-rail-head-close"
              />
            </div>
            <div className="composer-side-rail__head-body" data-datum="side.component" data-kind="component">
              {selection.body}
            </div>
          </>
        )}
        {!selection && idle && (
          <>
            {(idle.kind || idle.title) && (
              <div className="composer-side-rail__head" data-testid="composer-side-rail-idle-head">
                {idle.kind && <span className="composer-side-rail__head-kind">{idle.kind}</span>}
                {idle.title && <b className="composer-side-rail__head-title">{idle.title}</b>}
              </div>
            )}
            <div className="composer-side-rail__head-body" data-testid="composer-side-rail-idle">
              {idle.body}
            </div>
          </>
        )}
        {/* The tab body is HIDDEN, not unmounted, while a selection owns the rail: it holds the
            agent panel below, and unmounting it here tore the live conversation down every time
            the attach picker or a rule editor opened in the rail — the same Rule 7 loss the
            `hidden` mount on the agent panel exists to prevent across tab changes. */}
        <div
          className="composer-side-rail__body"
          data-datum="side.component#3"
          data-kind="component"
          data-container="side"
          hidden={!!selection || !!idle}
        >
          <div className="composer-side-rail__tabs" data-container="side > tabs">
            <SegmentedControlAtom
              options={tabOptions}
              value={effectiveTab}
              onChange={(value) => setTab(value as SideRailTab)}
              mode="single"
              size="small"
              ariaLabel={t(`${namespace}.ariaLabel`)}
              data-testid="composer-side-rail-tabs"
              asTabs
            />
          </div>

          {sampleContent && effectiveTab === 'sample' && (
            /* The panel itself is the focus target for the footer's `[Preview sample record]`
               (`:1912`): moving focus to a heading that repeats the tab's own label told a
               screen-reader user nothing the tab had not just said. */
            <div
              className="composer-side-rail__panel"
              role="tabpanel"
              tabIndex={-1}
              ref={setHeadingRef}
              data-testid="composer-side-rail-sample"
            >
              {sampleContent}
            </div>
          )}

          {effectiveTab === 'inherits' && (
            <div
              className="composer-side-rail__panel"
              role="tabpanel"
              tabIndex={-1}
              ref={inheritedPanelRef}
              data-testid="composer-side-rail-inherited"
              data-container="side > panel#3"
            >
              <p
                className="composer-side-rail__inherited-note"
                data-datum="side.everything-below-arrives-from-the-lineag"
                data-kind="hint"
                data-container="side > panel#3"
              >
                {t(`${namespace}.inheritedIntro`)}
              </p>
              {inheritedContent}
            </div>
          )}

          {/* Mounted for as long as there is a target to talk about, and HIDDEN rather than
              unmounted while another tab is showing. Conditional rendering tore down
              `AgentThreadOrganism` on every tab change, taking the whole conversation with it —
              its turns, its `conversationId`, and any stream still arriving — so a ghost's
              provenance link fired from the canvas always landed in an empty thread
              (`/standards/agentic/agentic-ux-standards.md` Rule 7: a proposal's origin has to stay
              inspectable). `hidden` keeps it out of the accessibility tree and out of the render
              captures (the walker skips `display: none` subtrees — scripts/lib/structure-walk.js), so the panel
              costs nothing in the states that do not show it. */}
          {/* `locked` unmounts rather than hides: the Rule 7 argument above is about preserving a
              conversation across tab changes, and a read-only type has no draft to propose against,
              so there is no conversation to lose. Left mounted it stood up a live
              `AgentThreadOrganism` — and its connection — for a platform-owned or sealed type. */}
          {!locked && agentTarget && (
            <div
              className="composer-side-rail__agent"
              role="tabpanel"
              hidden={effectiveTab !== 'agent'}
              tabIndex={-1}
              data-testid="composer-side-rail-agent"
              data-container="side > panel#2"
            >
              {/* The ghost that sent us here names a turn this thread does not hold — it can name
                  one from a conversation that has since been replaced, or one the thread never
                  carried. Said out loud, because the alternative is a provenance link that looks
                  live and does nothing when clicked. Carries no contract datum: it is a failure
                  state the design does not describe. */}
              {/* The region is rendered whether or not it has anything to say, and takes focus
                  when it does. Announcement alone would not be enough: the success path moves
                  focus onto the turn, so the failure path must not leave the reader three regions
                  away, on the button that was clicked. */}
              <p
                ref={originMissingRef}
                tabIndex={-1}
                className={`composer-side-rail__origin-missing${
                  originMissing ? ' composer-side-rail__origin-missing--shown' : ''
                }`}
                role="status"
                data-testid="composer-side-rail-origin-missing"
              >
                {originMissing ? t(`${agentNamespace}.originMissing`) : ''}
              </p>
              {/* `:1849-1869` — the queue states how much is waiting, offers the whole lot at once,
                  and says the two ways through it. The count rides the sentence, not the button,
                  so [Approve all] reads the same whether one block is waiting or nine. */}
              {pendingGhostCount > 0 && (
                <div className="composer-side-rail__queue" data-container="side > panel#2 > list">
                  <div
                    className="composer-side-rail__queue-head"
                    data-datum="side.pending"
                    data-kind="heading"
                  >
                    <strong
                      className="composer-side-rail__queue-count"
                      data-datum="side.pending#2"
                      data-kind="strong"
                    >
                      {t(`${agentNamespace}.pendingCount`, { count: pendingGhostCount })}
                    </strong>
                    <ButtonAtom
                      variant="text"
                      size="small"
                      className="composer-side-rail__approve-all"
                      onClick={onApproveAllGhosts}
                      loading={approveAllProgress !== null}
                      disabled={approveAllProgress !== null}
                      data-testid="composer-side-rail-approve-all"
                      data-datum="side.approve-all"
                      data-kind="button"
                    >
                      {t(`${agentNamespace}.approveAll`)}
                    </ButtonAtom>
                    <ButtonAtom
                      variant="text"
                      size="small"
                      onClick={() => setConfirmingDiscardAll(true)}
                      disabled={approveAllProgress !== null}
                      data-testid="composer-side-rail-discard-all"
                    >
                      {t(`${agentNamespace}.discardAll`)}
                    </ButtonAtom>
                    {/* The batch's working state, where the button that started it lives: both
                        controls are held while it runs, and this line is the reason they are. */}
                    {approveAllProgress && (
                      <span
                        className="composer-side-rail__queue-progress"
                        role="status"
                        data-testid="composer-side-rail-approve-all-progress"
                      >
                        {t(`${agentNamespace}.approveAllProgress`, approveAllProgress)}
                      </span>
                    )}
                  </div>
                  {confirmingDiscardAll && (
                    <InlineConfirmMolecule
                      tone="danger"
                      icon="pi-exclamation-triangle"
                      testId="composer-side-rail-discard-all-confirm"
                      titleId="composer-side-rail-discard-all-confirm-title"
                      title={t(`${agentNamespace}.discardAllConfirmTitle`)}
                      body={t(
                        pendingBlocks.length > 0
                          ? `${agentNamespace}.discardAllConfirmMessageInBlocks`
                          : `${agentNamespace}.discardAllConfirmMessage`,
                        {
                          count: discardAllCount ?? pendingGhostCount,
                          blocks: pendingBlocks.map((block) => block.label).join(', '),
                        },
                      )}
                      actions={[
                        {
                          label: t(`${agentNamespace}.discardAllConfirmAccept`),
                          onClick: () => {
                            setConfirmingDiscardAll(false);
                            onDiscardAllGhosts?.();
                          },
                          variant: 'danger',
                          'data-testid': 'composer-side-rail-discard-all-confirm-accept',
                        },
                        {
                          label: t('common.cancel'),
                          onClick: () => setConfirmingDiscardAll(false),
                          variant: 'text',
                          'data-testid': 'composer-side-rail-discard-all-confirm-cancel',
                        },
                      ]}
                    />
                  )}
                  {pendingBlocks.length > 0 && (
                    <div
                      className="composer-side-rail__queue-list"
                      data-container="side > panel#2 > list > list"
                    >
                      {pendingBlocks.map((block) => (
                        <ButtonAtom
                          key={block.id}
                          variant="secondary"
                          size="small"
                          className="composer-side-rail__queue-item"
                          onClick={() => onJumpToBlock?.(block.id)}
                          aria-label={t(`${agentNamespace}.queueJumpTo`, { block: block.label })}
                          data-container="each(pending)"
                          data-testid="composer-side-rail-queue-item"
                          data-datum="side.b-label"
                          data-kind="button"
                        >
                          {block.icon ? (
                            <span className={`${block.icon} composer-side-rail__queue-item-icon`} aria-hidden="true" />
                          ) : null}
                          <span className="composer-side-rail__queue-item-label">{block.label}</span>
                          <span className="pi pi-arrow-right composer-side-rail__queue-item-arrow" aria-hidden="true" />
                        </ButtonAtom>
                      ))}
                    </div>
                  )}
                  <p
                    className="composer-side-rail__queue-hint"
                    data-datum="side.walk-them-one-at-a-time-or-take-the-lot"
                    data-kind="hint"
                  >
                    {t(`${agentNamespace}.walkThem`)}
                  </p>
                </div>
              )}
              <AgentThreadOrganism
                ref={agentThreadRef}
                endpoint={agentEndpoint}
                getToken={agentGetToken}
                target={agentTarget}
                draftDigest={agentDraftDigest}
                onTypeEditProposal={onTypeEditProposal}
                onAvailabilityChange={handleAgentAvailability}
                onJumpToBlock={onJumpToBlock}
                initialItems={initialAgentItems}
                inputPlaceholder={t(`${agentNamespace}.inputPlaceholder`)}
                parity={parity}
                critique={critique}
                onDismissCritique={onDismissCritique}
              />
            </div>
          )}
        </div>
      </aside>
    );
  }
);

InspectorRailOrganism.displayName = 'InspectorRailOrganism';

export default InspectorRailOrganism;
